import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "p-10" }
const _hoisted_2 = {
  tabindex: "0",
  class: "focus:outline-none text-xl font-bold text-gray-900"
}
const _hoisted_3 = { class: "mt-8 grid grid-cols-2 gap-x-6 gap-y-8" }
const _hoisted_4 = {
  key: 0,
  class: "flex flex-row items-center justify-between border rounded py-4 px-5 h-12 self-end"
}
const _hoisted_5 = { class: "text-base text-gray-900 font-medium" }
const _hoisted_6 = { class: "mt-8" }

import { computed, onMounted, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { IOption, ISuggestion } from '@/lib';
import { Anatomy, Condition, Diagnosis, DiagnosisRequest, DiagnosisStatus } from '@/models';
import { BaseButton } from '@/lib/components/Button';
import { SearchInput } from '@/lib/components/Search';
import { BaseSelect } from '@/lib/components/Select';
import { BaseSwitch } from '@/lib/components/Switch';

enum AnatomyLaterality {
  RIGHTLATERALITY = 'R',
  LEFTLATERALITY = 'L',
  BOTHLATERALITY = 'BIL'
}


export default /*#__PURE__*/_defineComponent({
  __name: 'NewConditionForm',
  props: {
  conditionList: {
    type: Array as PropType<Array<Condition>>,
    default: () => []
  },
  conditions: {
    type: Array as PropType<Array<Diagnosis>>,
    default: () => []
  },
  anatomy: {
    type: Array as PropType<Array<Anatomy>>,
    default: () => []
  },
  statuses: {
    type: Array as PropType<Array<DiagnosisStatus>>,
    default: () => []
  },
  isPresent: {
    type: Boolean,
    default: true
  },
  diagnosis: {
    type: Object,
    default: null
  }
},
  emits: ["search", "loadNext", "cancel", "create"],
  setup(__props, { emit }: { emit: ({
  (e: 'search', value: string): void;
  (e: 'loadNext'): void;
  (e: 'cancel'): void;
  (e: 'create', value: DiagnosisRequest): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const { t } = useI18n();



const conditionId = ref('');
const stageId = ref('');
const locationId = ref('');
const statusId = ref('');
const search = ref('');
const differential = ref(false);

const title = computed(() => props.diagnosis
  ? t('custom.uhb.diagnosis.edit-condition')
  : props.isPresent
    ? t('custom.uhb.diagnosis.add-condition')
    : t('custom.uhb.diagnosis.add-absence'));
const saveButtonText = computed(() => props.diagnosis
  ? t('platform.common.save-changes')
  : props.isPresent
    ? t('custom.uhb.diagnosis.add-condition')
    : t('custom.uhb.diagnosis.add-absence'));
const statusOptions = computed<Array<IOption>>(() => props.statuses.map((status) => ({
  label: status.name,
  value: status.id
})));
const locationOptions = computed<Array<IOption>>(() => props.anatomy
  .filter((anatomy) => {
    if (condition.value) {
      const savedConditions = getSavedConditions();
      if (savedConditions.length > 0 && anatomy.laterality === AnatomyLaterality.BOTHLATERALITY) {
        return false;
      }

      const doesConditionAnatomieExist = savedConditions.find(
        (condition) => condition.anatomy && condition.anatomy.id === anatomy.id
      );

      return (
        !doesConditionAnatomieExist ||
        (props.diagnosis &&
          condition.value.id === props.diagnosis.condition.id &&
          props.diagnosis.anatomy.id === anatomy.id)
      );
    }
    return true;
  })
  .map((anatomy) => ({
    label: anatomy.name,
    value: anatomy.id
  })));
const stageOptions = computed<Array<IOption>>(() => stages.value.map((stage) => ({
  label: stage.name,
  value: stage.id
})));
const condition = computed(() => props.conditionList.find((condition) => condition.id === conditionId.value));
const location = computed(() => props.anatomy.find((anatomy) => anatomy.id === locationId.value));
const status = computed(() => props.statuses.find((status) => status.id === statusId.value));
const stage = computed(() => stages.value.find((stage) => stage.id === stageId.value));
const suggestions = computed<Array<ISuggestion>>(() =>
  props.conditionList.find((condition) => condition.name === search.value)
    ? []
    : props.conditionList.map((condition) => ({
      id: condition.id,
      label: condition.name
    }))
);
const disabled = computed(() => !condition.value || !location.value);
const stages = computed(() => condition.value ? condition.value.stages : []);

onMounted(() => {
  if (props.diagnosis) {
    const diagnosis = props.diagnosis as Diagnosis;
    conditionId.value = diagnosis.condition.id;
    search.value = diagnosis.condition.name;
    stageId.value = diagnosis.stage ? diagnosis.stage.id : '';
    locationId.value = diagnosis.anatomy ? diagnosis.anatomy.id : '';
    statusId.value = diagnosis.status ? diagnosis.status.id : '';
    differential.value = diagnosis.is_differential;
  }
});
const onSearch = (value: ISuggestion) => {
  stageId.value = '';
  search.value = value.label;
  conditionId.value = value.id;
  emit('search', search.value);
};
const resetValues = () => {
  if (search.value.length) {
    emit('search', '');
  }
  conditionId.value = '';
  search.value = '';
  differential.value = false;
  statusId.value = '';
  stageId.value = '';
  locationId.value = '';
};
const cancel = () => {
  resetValues();
  emit('cancel');
};
const save = () => {
  if (condition.value) {
    const genericDiagnosis: DiagnosisRequest = {
      ...(props.isPresent ? { is_differential: differential.value } : {}),
      ...(stage.value ? { stage_id: stage.value.id } : {}),
      ...(location.value ? { anatomy_id: location.value.id } : {}),
      ...(status.value ? { status_id: status.value.id } : {}),
      condition_id: condition.value.id,
      is_present: props.isPresent
    };

    // This logic only applies to the "Both Eyes" anatomy
    if (location.value?.laterality === AnatomyLaterality.BOTHLATERALITY) {
      const rightAnatomy = props.anatomy.find((location) => location.laterality === AnatomyLaterality.RIGHTLATERALITY);
      const leftAnatomy = props.anatomy.find((location) => location.laterality === AnatomyLaterality.LEFTLATERALITY);

      const rightDiagnosis = { ...genericDiagnosis };
      if (rightAnatomy) {
        rightDiagnosis.anatomy_id = rightAnatomy.id;
      }
      emit('create', rightDiagnosis);

      const leftDiagnosis = { ...genericDiagnosis };
      if (leftAnatomy) {
        leftDiagnosis.anatomy_id = leftAnatomy.id;
      }
      emit('create', leftDiagnosis);
    } else {
      emit('create', genericDiagnosis);
    }

    resetValues();
  }
};
const getSavedConditions = () => props.conditions
  .filter((diagnosis) => diagnosis.condition.id === (condition.value as Condition).id);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h1", _hoisted_2, _toDisplayString(_unref(title)), 1),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(SearchInput), {
        modelValue: search.value,
        "onUpdate:modelValue": [
          _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
          _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('search', $event)))
        ],
        class: "col-span-2",
        icon: "search",
        label: _ctx.$t('custom.uhb.diagnosis.condition'),
        placeholder: _ctx.$t('custom.uhb.diagnosis.search-condition'),
        suggestions: _unref(suggestions),
        disabled: !!__props.diagnosis,
        onSubmit: onSearch,
        onScrolled: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('loadNext')))
      }, null, 8, ["modelValue", "label", "placeholder", "suggestions", "disabled"]),
      _createVNode(_unref(BaseSelect), {
        modelValue: locationId.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((locationId).value = $event)),
        placeholder: _ctx.$t('platform.common.enter-value'),
        class: _normalizeClass({ 'col-span-2': !__props.isPresent }),
        options: _unref(locationOptions),
        label: _ctx.$t('platform.patient.location')
      }, null, 8, ["modelValue", "placeholder", "class", "options", "label"]),
      (__props.isPresent)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t('custom.uhb.diagnosis.differential')), 1),
            _createVNode(_unref(BaseSwitch), {
              modelValue: differential.value,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((differential).value = $event))
            }, null, 8, ["modelValue"])
          ]))
        : _createCommentVNode("", true),
      (__props.isPresent)
        ? (_openBlock(), _createBlock(_unref(BaseSelect), {
            key: 1,
            modelValue: stageId.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((stageId).value = $event)),
            placeholder: _ctx.$t('platform.common.enter-value'),
            options: _unref(stageOptions),
            label: _ctx.$t('custom.uhb.diagnosis.stage'),
            disabled: !_unref(condition)
          }, null, 8, ["modelValue", "placeholder", "options", "label", "disabled"]))
        : _createCommentVNode("", true),
      (__props.isPresent)
        ? (_openBlock(), _createBlock(_unref(BaseSelect), {
            key: 2,
            modelValue: statusId.value,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((statusId).value = $event)),
            placeholder: _ctx.$t('platform.common.enter-value'),
            options: _unref(statusOptions),
            label: _ctx.$t('platform.common.status')
          }, null, 8, ["modelValue", "placeholder", "options", "label"]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(_unref(BaseButton), {
        size: "large",
        disabled: _unref(disabled),
        onClick: save
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(saveButtonText)), 1)
        ]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(_unref(BaseButton), {
        size: "large",
        color: "ghost",
        class: "ml-4",
        onClick: cancel
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('platform.common.cancel')), 1)
        ]),
        _: 1
      })
    ])
  ]))
}
}

})